/**
 * Module for work with LocalData (if it is available) or cookies (if LocalData isn't available).
 * @author WebMechanic (20.05.13).
 */

/**
 * Пример установки куки на год:
 * localdata.setCookie('nameCookie', 'value', {'expires': 31536000}).
 * @param {string} key
 * @param {*} valueRaw
 * @param {Object} props
 * @param {boolean} setRawValue
 */
export function setCookie(key, valueRaw, props, setRawValue) {
    const value = setRawValue ? valueRaw : encodeURIComponent(valueRaw);
    const innerProps = props || {};

    let exp = innerProps.expires;
    let d;
    let updatedCookie = `${key}=${value}`;

    if (typeof exp === 'number' && exp) {
        d = new Date();
        d.setTime(d.getTime() + exp * 1000);
        innerProps.expires = d;
        exp = d;
    }
    if (exp && exp.toUTCString) {
        innerProps.expires = exp.toUTCString();
    }

    Object.entries(innerProps).forEach(([propKey, propValue]) => {
        updatedCookie += `; ${propKey}`;
        if (propValue !== true) {
            updatedCookie += `=${propValue}`;
        }
    });

    document.cookie = updatedCookie;
}

/**
 * @param {string} key
 * @returns {string}
 */
export function getCookie(key) {
    const matches = document.cookie.match(
        new RegExp(
            `(?:^|; )${key.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`
        )
    );
    return matches ? decodeURIComponent(matches[1]) : undefined;
}

/**
 * @param {string} key
 * @param {string} path
 */
export function deleteCookie(key, path) {
    setCookie(key, '', {
        path: path || '/',
        expires: -1
    });
}

/**
 * @param {string} type - storage type
 * @returns {boolean}
 */
export function isStorageAvailable(type) {
    const storage = window[type];
    const x = '__storage_test__';
    try {
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    } catch (e) {
        return (
            e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === 'QuotaExceededError' ||
                // Firefox
                e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage.length !== 0
        );
    }
}

/**
 * @returns {boolean}
 */
export const isLocalStorageAvailable = (() => {
    // Замыкание для хранения результата ранее выполненной проверки
    let testResult = null;
    return () => {
        if (testResult === null) {
            testResult = isStorageAvailable('localStorage');
        }
        return testResult;
    };
})();

/**
 * @returns {boolean}
 */
export const isSessionStorageAvailable = (() => {
    // Замыкание для хранения результата ранее выполненной проверки
    let testResult = null;
    return () => {
        if (testResult === null) {
            testResult = isStorageAvailable('sessionStorage');
        }
        return testResult;
    };
})();

/**
 * Set data to local storage or cookies.
 * @param {string} key
 * @param {string} value
 * @param {string} [type]
 */
export function setLocalData(key, value, type = 'localStorage') {
    if (type == 'localStorage' && isLocalStorageAvailable()) {
        localStorage.setItem(key, value);
        return;
    }
    if (type == 'sessionStorage' && isSessionStorageAvailable()) {
        sessionStorage.setItem(key, value);
        return;
    }
    const date = new Date();
    const year = date.getFullYear();
    setCookie(key, value, {
        expires: date.setYear(year + 1),
        path: '/'
    });
}

/**
 * Get local data from local storage or cookies.
 * @param {string} key
 * @param {string} [type]
 * @returns {string}
 */
export function getLocalData(key, type = 'localStorage') {
    if (type == 'localStorage' && isLocalStorageAvailable()) {
        return localStorage.getItem(key);
    }
    if (type == 'sessionStorage' && isSessionStorageAvailable()) {
        return sessionStorage.getItem(key);
    } else {
        return getCookie(key);
    }
}

/**
 * Delete local data from storage.
 * @param {string} key
 * @param {string} [type]
 */
export function deleteLocalData(key, type = 'localStorage') {
    if (type == 'localStorage' && isLocalStorageAvailable()) {
        localStorage.removeItem(key);
        return;
    }
    if (type == 'sessionStorage' && isSessionStorageAvailable()) {
        sessionStorage.removeItem(key);
        return;
    }
    setCookie(key, null, { expires: -1 });
}

/**
 * Check if the key is present in local or session storage.
 * @param {string} key
 * @returns {boolean}
 */
export function isPopulated(key) {
    return (
        Boolean(getLocalData(key, 'localStorage')) ||
        Boolean(getLocalData(key, 'sessionStorage'))
    );
}

/**
 * Check if storage is empty.
 * @param {string} key
 */
export function isEmptyStorage(key) {
    return !isPopulated(key);
}
